import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownIcon,
  ArrowPathIcon,
  ArrowUpIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  PhoneIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { FaDog } from 'react-icons/fa';
import apiClient from '../utils/apiClient';

const CARD_BG = '#1c2432';

const DogIcon: React.FC<{ className?: string }> = ({ className }) => {
  const Icon = FaDog as React.ComponentType<{ className?: string }>;
  return <Icon className={className} />;
};

type Status =
  | 'neu'
  | 'gelesen'
  | 'kontaktiert'
  | 'warteliste'
  | 'aktiv'
  | 'abgelehnt'
  | 'abgemeldet'
  | 'spam';

type WaitlistMessage = {
  id: number;
  name: string;
  email: string;
  phone: string | null;
  dog_name: string | null;
  dog_breed: string | null;
  message: string;
  ip_address: string | null;
  status: Status;
  notes: string | null;
  contacted_at: string | null;
  status_changed_at: string | null;
  status_changed_by: string | null;
  email_sent: number;
  email_error: string | null;
  unsubscribe_token: string | null;
  unsubscribed_at: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

type Stats = Record<Status, number> & { total: number };

const STATUS_META: Record<Status, { label: string; chip: string; dot: string; pillTone: string }> = {
  neu:         { label: 'Neu',          chip: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', dot: 'bg-emerald-500', pillTone: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/15' },
  gelesen:     { label: 'Gelesen',      chip: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',         dot: 'bg-blue-500',    pillTone: 'text-blue-300 border-blue-500/30 bg-blue-500/15' },
  kontaktiert: { label: 'Kontaktiert',  chip: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200', dot: 'bg-indigo-500',  pillTone: 'text-indigo-300 border-indigo-500/30 bg-indigo-500/15' },
  warteliste:  { label: 'Warteliste',   chip: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',     dot: 'bg-amber-500',   pillTone: 'text-amber-300 border-amber-500/30 bg-amber-500/15' },
  aktiv:       { label: 'Aktiv',        chip: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',     dot: 'bg-green-500',   pillTone: 'text-primary-300 border-primary-500/30 bg-primary-500/15' },
  abgelehnt:   { label: 'Abgelehnt',    chip: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',         dot: 'bg-rose-500',    pillTone: 'text-rose-300 border-rose-500/30 bg-rose-500/15' },
  abgemeldet:  { label: 'Abgemeldet',   chip: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',            dot: 'bg-gray-400',    pillTone: 'text-slate-300 border-white/15 bg-white/5' },
  spam:        { label: 'Spam',         chip: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',             dot: 'bg-red-500',     pillTone: 'text-red-300 border-red-500/30 bg-red-500/15' },
};

const STATUS_ORDER: Status[] = ['neu', 'gelesen', 'kontaktiert', 'warteliste', 'aktiv', 'abgelehnt', 'abgemeldet', 'spam'];

const PAGE_SIZE = 25;

const MONTH_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

// Email-templates with placeholder support: {{name}}, {{hund}}, {{rasse}}
type EmailTemplate = {
  id: string;
  label: string;
  desc: string;
  subject: string;
  body: string;
};

const EMAIL_TEMPLATES: EmailTemplate[] = [
  {
    id: 'willkommen',
    label: 'Willkommen',
    desc: 'Begrüßung · nächste Schritte · Termin-Vorschlag',
    subject: 'Willkommen bei der HSV — deine Bewerbung',
    body: `Liebe/r {{name}},

vielen Dank für deine Bewerbung bei der HSV. Wir freuen uns auf dich{{hund_zusatz}}.

Komm gerne mal Samstag um 10 Uhr unverbindlich vorbei — dann lernen wir uns alle in Ruhe kennen.

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'rueckfrage',
    label: 'Rückfrage',
    desc: 'Mehr Infos zum Hund / zur Verfügbarkeit',
    subject: 'Rückfrage zu deiner Bewerbung — HSV',
    body: `Hallo {{name}},

danke für deine Anfrage. Damit wir dich gut einordnen können, hätten wir noch ein paar Rückfragen{{hund_zusatz}}:

· Wie alt ist dein Hund genau?
· Hast du schon Hundeschul-Erfahrung?
· An welchen Wochentagen hättest du Zeit?

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'warteliste',
    label: 'Auf Warteliste',
    desc: 'Eingang bestätigen · ETA grob',
    subject: 'Du bist auf der Warteliste — HSV',
    body: `Hallo {{name}},

deine Anfrage haben wir erhalten und auf die Warteliste gesetzt. Aktuell sind unsere Kurse voll — sobald ein Platz frei wird, melden wir uns sofort.

Erfahrungsgemäß dauert das 4–8 Wochen.

Liebe Grüße
Vorstand HSV`,
  },
  {
    id: 'ablehnung',
    label: 'Höfliche Ablehnung',
    desc: 'Volle Plätze · Empfehlung anderer Vereine',
    subject: 'Deine Bewerbung bei der HSV',
    body: `Hallo {{name}},

leider können wir dich aktuell nicht aufnehmen — alle Kurse sind voll und auch unsere Warteliste ist sehr lang.

Wir empfehlen dir, dich bei benachbarten Hundesportvereinen umzuschauen.

Alles Gute für dich{{hund_zusatz}}!

Liebe Grüße
Vorstand HSV`,
  },
];

function formatDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatLongDate(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'long', year: 'numeric' });
}

function buildDogInfo(m: WaitlistMessage): string {
  return [m.dog_name, m.dog_breed].filter(Boolean).join(' · ');
}

function daysSince(iso: string | null): number {
  if (!iso) return 0;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function initials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map(p => p.charAt(0).toUpperCase()).join('') || '?';
}

// hash name -> color tone for avatar
const AVATAR_TONES = [
  'bg-primary-500/20 border-primary-500/40 text-primary-300',
  'bg-blue-500/20 border-blue-500/40 text-blue-300',
  'bg-purple-500/20 border-purple-500/40 text-purple-300',
  'bg-amber-500/20 border-amber-500/40 text-amber-300',
  'bg-rose-500/20 border-rose-500/40 text-rose-300',
  'bg-cyan-500/20 border-cyan-500/40 text-cyan-300',
  'bg-pink-500/20 border-pink-500/40 text-pink-300',
  'bg-emerald-500/20 border-emerald-500/40 text-emerald-300',
  'bg-orange-500/20 border-orange-500/40 text-orange-300',
  'bg-teal-500/20 border-teal-500/40 text-teal-300',
];

function avatarTone(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

type DateTileVariant = 'amber' | 'emerald' | 'primary';

function dateTileVariant(days: number): DateTileVariant {
  if (days > 21) return 'amber';
  if (days <= 7) return 'emerald';
  return 'primary';
}

const DATE_TILE_STYLES: Record<DateTileVariant, { wrap: string; kicker: string; tail: string }> = {
  amber: {
    wrap: 'border-amber-500/30',
    kicker: 'text-amber-300',
    tail: 'text-amber-300/80',
  },
  emerald: {
    wrap: 'border-emerald-500/30',
    kicker: 'text-emerald-300',
    tail: 'text-emerald-300/80',
  },
  primary: {
    wrap: 'border-primary-500/30',
    kicker: 'text-primary-300',
    tail: 'text-primary-300/80',
  },
};

const DATE_TILE_BG: Record<DateTileVariant, string> = {
  amber:   'linear-gradient(180deg, rgba(245,158,11,0.16) 0%, rgba(245,158,11,0.04) 100%)',
  emerald: 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)',
  primary: 'linear-gradient(180deg, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.04) 100%)',
};

const DateTile: React.FC<{ iso: string | null }> = ({ iso }) => {
  const days = daysSince(iso);
  const variant = dateTileVariant(days);
  const styles = DATE_TILE_STYLES[variant];
  const d = iso ? new Date(iso) : null;
  const validDate = d && !Number.isNaN(d.getTime());
  const month = validDate ? MONTH_SHORT[d.getMonth()] : '—';
  const day = validDate ? d.getDate().toString().padStart(2, '0') : '—';

  let tailLabel: string;
  if (days === 0) tailLabel = 'Heute';
  else if (days === 1) tailLabel = '1 Tg';
  else tailLabel = `${days} Tg`;

  return (
    <div
      className={`shrink-0 flex flex-col items-center justify-center rounded-2xl border ${styles.wrap}`}
      style={{
        width: 64,
        height: 76,
        background: DATE_TILE_BG[variant],
      }}
    >
      <div className={`text-[9px] uppercase tracking-widest font-bold ${styles.kicker}`}>{month}</div>
      <div className="text-2xl font-black text-gray-900 dark:text-white tabular-nums leading-none">{day}</div>
      <div className={`text-[9px] font-semibold mt-0.5 ${styles.tail}`}>{tailLabel}</div>
    </div>
  );
};

const WaitlistManagement: React.FC = () => {
  const [items, setItems] = useState<WaitlistMessage[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<Status | 'alle'>('alle');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [sortMode, setSortMode] = useState<'oldest' | 'newest' | 'status'>('oldest');

  const [selected, setSelected] = useState<WaitlistMessage | null>(null);
  const [emailTarget, setEmailTarget] = useState<WaitlistMessage | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (statusFilter !== 'alle') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();

      const res = await apiClient.get('/api/waitlist', { params });
      if (res.data && res.data.success) {
        setItems(res.data.messages || []);
        setTotal(res.data.total || 0);
        setStats(res.data.stats || null);
      } else {
        setError(res.data?.message || 'Fehler beim Laden');
      }
    } catch (err: any) {
      // 404 = Endpoint noch nicht implementiert → still Empty-State zeigen, kein Banner
      if (err.response?.status === 404) {
        setItems([]);
        setTotal(0);
        setStats(null);
      } else {
        setError(err.response?.data?.message || err.message || 'Netzwerkfehler');
      }
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Suche debouncen (300ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleStatusFilter = (s: Status | 'alle') => {
    setStatusFilter(s);
    setPage(0);
  };

  const refreshKeepingSelection = async (id?: number) => {
    await fetchData();
    if (id != null) {
      try {
        const res = await apiClient.get(`/api/waitlist/${id}`);
        if (res.data?.success) setSelected(res.data.message);
      } catch {
        /* ignore */
      }
    }
  };

  const handleMove = async (id: number, dir: 'up' | 'down') => {
    try {
      await apiClient.post(`/api/waitlist/${id}/move-${dir}`);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Sortieren fehlgeschlagen');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Diesen Eintrag wirklich löschen?')) return;
    try {
      await apiClient.delete(`/api/waitlist/${id}`);
      setSelected(null);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Löschen fehlgeschlagen');
    }
  };

  // Quick approve / reject from list (status update)
  const quickStatus = async (id: number, newStatus: Status) => {
    try {
      await apiClient.patch(`/api/waitlist/${id}`, { status: newStatus });
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Status-Änderung fehlgeschlagen');
    }
  };

  const visibleStats = useMemo(() => {
    if (!stats) return [];
    return STATUS_ORDER.map(s => ({ status: s, count: stats[s] || 0 }));
  }, [stats]);

  // Sorted items based on sortMode (client-side ordering, server returns by sort_order)
  const sortedItems = useMemo(() => {
    const arr = [...items];
    if (sortMode === 'oldest') {
      arr.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    } else if (sortMode === 'newest') {
      arr.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortMode === 'status') {
      const ord = (s: Status) => STATUS_ORDER.indexOf(s);
      arr.sort((a, b) => ord(a.status) - ord(b.status));
    }
    return arr;
  }, [items, sortMode]);

  // Story metrics for intro
  const storyMetrics = useMemo(() => {
    const open = (stats?.neu || 0) + (stats?.gelesen || 0);
    const thisWeek = items.filter(m => daysSince(m.created_at) <= 7).length;
    const oldest = items.reduce((max, m) => Math.max(max, daysSince(m.created_at)), 0);
    const withDog = items.filter(m => !!(m.dog_name || m.dog_breed)).length;
    const trainerInterest = items.filter(m =>
      (m.message || '').toLowerCase().match(/\btrainer|co-trainer|assistent\b/)
    ).length;
    return { open, thisWeek, oldest, withDog, trainerInterest };
  }, [stats, items]);

  // Hero applicant: oldest unprocessed (neu/gelesen) — fallback to oldest in list
  const heroApplicant = useMemo<WaitlistMessage | null>(() => {
    if (sortedItems.length === 0) return null;
    const open = sortedItems
      .filter(m => m.status === 'neu' || m.status === 'gelesen')
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
    if (open.length > 0) return open[0];
    return [...sortedItems].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    )[0];
  }, [sortedItems]);

  const monthLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen font-sans text-gray-800 dark:text-slate-200" style={{ background: 'transparent' }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-8 sm:pb-10">

        {/* INTRO — Kicker + Story + 3 Stats */}
        <section className="pt-2 pb-6">
          <div className="text-[11px] uppercase tracking-[0.22em] text-amber-400/80 font-semibold mb-3">
            {storyMetrics.open} offene {storyMetrics.open === 1 ? 'Bewerbung' : 'Bewerbungen'} · Stand {monthLabel}
          </div>
          <h2
            className="text-4xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.02]"
            style={{ letterSpacing: '-0.04em' }}
          >
            Wer dazu<br />
            <span className="text-amber-600 dark:text-amber-400">will.</span>
          </h2>
          <p className="text-gray-600 dark:text-slate-400 text-base md:text-lg leading-relaxed mt-5 max-w-2xl">
            {(() => {
              const parts: React.ReactNode[] = [];
              parts.push(
                <React.Fragment key="open">
                  <span className="font-bold text-gray-900 dark:text-white">{storyMetrics.open}</span>{' '}
                  {storyMetrics.open === 1 ? 'Bewerber' : 'Bewerber'} in der Pipeline
                </React.Fragment>
              );
              if (storyMetrics.oldest > 0) {
                parts.push(
                  <React.Fragment key="oldest">
                    , älteste Anfrage seit{' '}
                    <span className={`font-bold ${storyMetrics.oldest > 21 ? 'text-amber-300' : 'text-white'}`}>
                      {storyMetrics.oldest} {storyMetrics.oldest === 1 ? 'Tag' : 'Tage'}
                    </span>
                  </React.Fragment>
                );
              }
              if (storyMetrics.withDog > 0) {
                parts.push(
                  <React.Fragment key="dog">
                    {' '}·{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{storyMetrics.withDog}</span> mit Hund
                  </React.Fragment>
                );
              }
              if (storyMetrics.trainerInterest > 0) {
                parts.push(
                  <React.Fragment key="trainer">
                    {' '}·{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{storyMetrics.trainerInterest}</span> mit
                    Trainer-Interesse
                  </React.Fragment>
                );
              }
              if (heroApplicant) {
                parts.push(
                  <React.Fragment key="hero">
                    . Aktuell im Spotlight:{' '}
                    <span className="font-bold text-gray-900 dark:text-white">{heroApplicant.name}</span>{' '}
                    — wartet seit{' '}
                    <span className="font-bold text-gray-900 dark:text-white">
                      {daysSince(heroApplicant.created_at)}{' '}
                      {daysSince(heroApplicant.created_at) === 1 ? 'Tag' : 'Tagen'}
                    </span>
                  </React.Fragment>
                );
              }
              return <>{parts}.</>;
            })()}
          </p>
        </section>

        {/* Error Banner */}
        {error && (
          <section className="pb-6">
            <div
              className="rounded-2xl px-4 py-3 flex items-start gap-3"
              style={{
                background: 'rgba(245,158,11,0.10)',
                border: '1px solid rgba(245,158,11,0.30)',
              }}
            >
              <span className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 grid place-items-center flex-shrink-0">
                <ExclamationTriangleIcon className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-amber-200">Daten konnten nicht geladen werden</p>
                <p className="text-xs text-amber-300/80 mt-0.5">{error}</p>
              </div>
              <button
                onClick={() => fetchData()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold transition"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                Erneut
              </button>
            </div>
          </section>
        )}

        {/* HERO — Aktuelle Bewerbung */}
        {heroApplicant && (
          <section className="pb-6">
            <div className="mb-4 flex items-end justify-between gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">
                  Spotlight
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight mt-1">
                  Aktuelle Bewerbung
                </h3>
              </div>
              {(heroApplicant.status === 'neu' || heroApplicant.status === 'gelesen') && (
                <span className="hidden md:inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                  noch unbearbeitet
                </span>
              )}
            </div>

            <article
              className="group relative rounded-3xl overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
              style={{ boxShadow:
                  '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
            >
              <div className="grid md:grid-cols-[6fr_7fr]">
                {/* LEFT: Avatar / initial panel */}
                <div className="relative overflow-hidden aspect-[4/5] md:aspect-auto md:min-h-[480px] flex items-center justify-center"
                     style={{
                       background:
                         'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(2,6,23,0.55) 100%)',
                     }}
                >
                  <div className="text-white/85 font-black select-none"
                       style={{ fontSize: 'clamp(6rem, 14vw, 11rem)', letterSpacing: '-0.04em', textShadow: '0 4px 24px rgba(0,0,0,0.55)' }}
                  >
                    {initials(heroApplicant.name)}
                  </div>

                  {/* Mobile fade */}
                  <div className="absolute inset-0 pointer-events-none md:hidden"
                       style={{
                         background:
                           'linear-gradient(180deg, rgba(2,6,23,0.05) 0%, rgba(2,6,23,0) 30%, rgba(2,6,23,0.55) 65%, rgba(28,36,50,0.98) 100%)',
                       }}
                  />
                  {/* Desktop vignette */}
                  <div className="hidden md:block absolute inset-0 pointer-events-none"
                       style={{
                         background:
                           'linear-gradient(180deg, rgba(2,6,23,0.30) 0%, rgba(2,6,23,0) 18%, rgba(2,6,23,0) 78%, rgba(2,6,23,0.45) 100%)',
                       }}
                  />

                  {/* Desktop floating eingangs-badge */}
                  <div className="hidden md:flex absolute bottom-5 left-5 right-5 items-end justify-between gap-3 z-10">
                    <div className="flex items-center gap-2.5 px-3 py-2 rounded-2xl backdrop-blur-md border border-gray-200 dark:border-white/10"
                         style={{ background: 'rgba(2,6,23,0.70)' }}
                    >
                      <div className="w-9 h-9 rounded-xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                        <ClipboardDocumentListIcon className="w-4 h-4 text-amber-700 dark:text-amber-300" />
                      </div>
                      <div>
                        <div className="text-[9px] uppercase tracking-widest text-slate-300/90 font-bold">Eingang</div>
                        <div className="text-sm font-black text-gray-900 dark:text-white tabular-nums">
                          {formatLongDate(heroApplicant.created_at)}
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-semibold border border-amber-500/40 bg-amber-500/15 text-amber-700 dark:text-amber-300 backdrop-blur">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                      {daysSince(heroApplicant.created_at)} {daysSince(heroApplicant.created_at) === 1 ? 'Tag' : 'Tage'} offen
                    </span>
                  </div>

                  {/* Mobile overlay top pills */}
                  <div className="md:hidden absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 pt-5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur ${STATUS_META[heroApplicant.status].pillTone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[heroApplicant.status].dot} animate-pulse`} />
                        {STATUS_META[heroApplicant.status].label}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-700 dark:text-primary-300 backdrop-blur">
                        {daysSince(heroApplicant.created_at)} Tg
                      </span>
                    </div>
                  </div>
                  <div className="md:hidden absolute inset-x-0 bottom-0 px-5 pb-5 z-10">
                    {buildDogInfo(heroApplicant) && (
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/20 bg-white/10 text-gray-900 dark:text-white backdrop-blur">
                          {buildDogInfo(heroApplicant)}
                        </span>
                      </div>
                    )}
                    <h3 className="text-gray-900 dark:text-white font-black leading-none"
                        style={{ fontSize: 'clamp(2rem, 7vw, 2.5rem)', letterSpacing: '-0.04em', textShadow: '0 1px 2px rgba(0,0,0,0.55), 0 4px 18px rgba(0,0,0,0.55)' }}
                    >
                      {heroApplicant.name}
                    </h3>
                    <p className="text-white/95 text-sm leading-relaxed mt-2"
                       style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
                    >
                      Bewirbt sich seit{' '}
                      <span className="font-bold text-amber-700 dark:text-amber-300">{formatLongDate(heroApplicant.created_at)}</span>
                    </p>
                  </div>
                </div>

                {/* RIGHT: Content */}
                <div className="relative hidden md:flex flex-col justify-between p-8 lg:p-10">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_META[heroApplicant.status].pillTone}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[heroApplicant.status].dot} animate-pulse`} />
                        {STATUS_META[heroApplicant.status].label}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-semibold border border-white/15 bg-white/5 text-gray-700 dark:text-slate-300">
                        seit {daysSince(heroApplicant.created_at)} {daysSince(heroApplicant.created_at) === 1 ? 'Tag' : 'Tagen'}
                      </span>
                      {buildDogInfo(heroApplicant) && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-primary-400/30 bg-primary-500/15 text-primary-700 dark:text-primary-300">
                          <DogIcon className="w-3 h-3" />
                          {buildDogInfo(heroApplicant)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="my-6 lg:my-8">
                    <h2 className="text-gray-900 dark:text-white font-black leading-[1.02]"
                        style={{ fontSize: 'clamp(2.5rem, 4vw, 4.5rem)', letterSpacing: '-0.04em' }}
                    >
                      {heroApplicant.name}
                    </h2>
                    <p className="text-gray-700 dark:text-slate-300 text-base lg:text-lg leading-relaxed mt-4 max-w-xl line-clamp-4">
                      {heroApplicant.message || 'Keine Nachricht hinterlegt.'}
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Wartetage</div>
                      <div className={`text-lg font-black tabular-nums mt-0.5 ${
                        daysSince(heroApplicant.created_at) > 21 ? 'text-amber-400'
                        : daysSince(heroApplicant.created_at) <= 7 ? 'text-emerald-400'
                        : 'text-white'
                      }`}>
                        {daysSince(heroApplicant.created_at)}
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                        seit {formatLongDate(heroApplicant.created_at)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Hund</div>
                      <div className="text-lg font-black text-gray-900 dark:text-white mt-0.5 truncate">
                        {heroApplicant.dog_name || '—'}
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5 truncate">
                        {heroApplicant.dog_breed || 'kein Hund'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Status</div>
                      <div className="text-lg font-black text-gray-900 dark:text-white mt-0.5">
                        {STATUS_META[heroApplicant.status].label}
                      </div>
                      <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">
                        {heroApplicant.email_sent === 0 ? 'E-Mail-Fehler' : 'Bestätigt'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* HERO Footer Actions */}
              <div className="relative px-5 sm:px-8 py-5 md:py-6 border-t border-slate-800 bg-white dark:bg-[#1c2432]" >
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-6 items-center">
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Eingang</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 leading-tight">
                      {formatLongDate(heroApplicant.created_at)}
                      <br />
                      <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">
                        {daysSince(heroApplicant.created_at)} {daysSince(heroApplicant.created_at) === 1 ? 'Tag' : 'Tage'} offen
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Kontakt</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 truncate leading-tight">
                      {heroApplicant.email}
                      <br />
                      <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                        {heroApplicant.phone || '— keine Tel.'}
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Hund</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 leading-tight truncate">
                      {heroApplicant.dog_name || '—'}
                      <br />
                      <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                        {heroApplicant.dog_breed || 'kein Hund hinterlegt'}
                      </span>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Status</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white mt-1 leading-tight">
                      {STATUS_META[heroApplicant.status].label}
                      <br />
                      <span className="text-[11px] text-gray-600 dark:text-slate-400 font-medium">
                        {heroApplicant.status_changed_at ? `Geändert ${formatDate(heroApplicant.status_changed_at)}` : 'Unverändert'}
                      </span>
                    </div>
                  </div>
                  <div className="col-span-2 md:col-span-2 flex gap-2">
                    <button
                      onClick={() => setEmailTarget(heroApplicant)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
                    >
                      <EnvelopeIcon className="w-3.5 h-3.5" />
                      Kontaktieren
                    </button>
                    <button
                      onClick={() => quickStatus(heroApplicant.id, 'abgelehnt')}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 text-xs font-bold transition"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                      Ablehnen
                    </button>
                    <button
                      onClick={() => quickStatus(heroApplicant.id, 'aktiv')}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-3 rounded-2xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition"
                    >
                      <CheckCircleIcon className="w-3.5 h-3.5" />
                      Bestätigen
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </section>
        )}

        {/* FILTER TOOLBAR */}
        <section className="pb-6">
          <div
            className="rounded-3xl p-4 md:p-5 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
          >
            <div className="flex flex-wrap items-center gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[240px]">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 dark:text-slate-500" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Name, E-Mail, Hund, Notiz …"
                  className="w-full h-11 pl-9 pr-3 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-gray-900 dark:text-white placeholder:text-slate-500 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition"
                />
              </div>
              {/* Sort */}
              <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-500 dark:text-slate-500 hidden sm:inline">Sortierung:</span>
                {([
                  { id: 'oldest' as const, label: 'Älteste' },
                  { id: 'newest' as const, label: 'Neueste' },
                  { id: 'status' as const, label: 'Status' },
                ]).map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setSortMode(opt.id)}
                    className={`px-3 py-2 rounded-xl border font-semibold transition ${
                      sortMode === opt.id
                        ? 'bg-primary-500/15 border-primary-500/40 text-primary-300'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <button
                onClick={() => fetchData()}
                className="inline-flex items-center gap-2 h-11 px-3 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
                title="Aktualisieren"
              >
                <ArrowPathIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Aktualisieren</span>
              </button>
            </div>

            {/* Filter pills */}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => handleStatusFilter('alle')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  statusFilter === 'alle'
                    ? 'bg-primary-500 border-primary-500 text-slate-950'
                    : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                }`}
              >
                Alle {stats ? <span className="opacity-60">({stats.total})</span> : null}
              </button>
              {STATUS_ORDER.map(s => {
                const meta = STATUS_META[s];
                const active = statusFilter === s;
                return (
                  <button
                    key={s}
                    onClick={() => handleStatusFilter(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition ${
                      active
                        ? 'bg-primary-500 border-primary-500 text-slate-950'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                    {stats ? <span className="opacity-60">({stats[s] || 0})</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* APPLICANT LIST */}
        <section className="pb-6">
          <div className="mb-5 flex items-end justify-between gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold">
                Pipeline · {total} {total === 1 ? 'Eintrag' : 'Einträge'}
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight mt-1">Wer wartet</h3>
            </div>
            <div className="text-xs text-gray-500 dark:text-slate-500 hidden md:block">
              Sortiert nach{' '}
              <span className="font-bold text-gray-900 dark:text-white">
                {sortMode === 'oldest' ? 'Eingangsdatum (älteste)' : sortMode === 'newest' ? 'Eingangsdatum (neueste)' : 'Status'}
              </span>
            </div>
          </div>

          {loading && items.length === 0 ? (
            <div
              className="rounded-3xl p-10 text-center text-gray-600 dark:text-slate-400 bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              Wird geladen …
            </div>
          ) : items.length === 0 ? (
            <article
              className="rounded-3xl p-10 md:p-14 text-center relative overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
            >
              <div className="absolute -top-12 -right-12 w-44 h-44 bg-primary-500/10 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-12 -left-12 w-44 h-44 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
              <div className="relative">
                <div className="w-20 h-20 rounded-3xl bg-primary-500/15 border border-primary-500/40 mx-auto mb-6 flex items-center justify-center">
                  <ClipboardDocumentListIcon className="w-10 h-10 text-primary-700 dark:text-primary-300" />
                </div>
                <h4 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white tracking-tight">Keine Einträge</h4>
                <p className="text-gray-600 dark:text-slate-400 mt-3 max-w-md mx-auto">
                  Aktuell liegen keine Anfragen aus dem Kontaktformular vor.
                </p>
              </div>
            </article>
          ) : (
            <div className="space-y-3">
              {sortedItems.map((m, idx) => {
                const meta = STATUS_META[m.status];
                const dog = buildDogInfo(m);
                const days = daysSince(m.created_at);
                const tone = avatarTone(m.name);
                const isOldest = days > 21;
                const isHero = heroApplicant?.id === m.id;
                const isToday = days === 0;
                const borderClass = isHero
                  ? 'border-primary-500/40'
                  : isOldest
                  ? 'border-amber-500/30'
                  : isToday
                  ? 'border-emerald-500/30'
                  : 'border-slate-700';

                return (
                  <article
                    key={m.id}
                    className={`bg-white dark:bg-[#1c2432] ${`rounded-2xl p-4 md:p-5 transition-colors hover:bg-white/[0.025] border ${borderClass}`}`}
                    style={{ boxShadow: '0 1px 0 0 rgba(255,255,255,0.04) inset, 0 30px 60px -20px rgba(0,0,0,0.55)' }}
                  >
                    <div className="flex items-start gap-3 md:gap-4">
                      {/* Sort buttons (mobile-hidden) */}
                      <div className="hidden md:flex flex-col gap-1 pt-1">
                        <button
                          onClick={() => handleMove(m.id, 'up')}
                          disabled={idx === 0 && page === 0}
                          title="Nach oben"
                          className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 text-gray-600 dark:text-slate-400 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                          <ArrowUpIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMove(m.id, 'down')}
                          title="Nach unten"
                          className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 text-gray-600 dark:text-slate-400 hover:bg-white/10 flex items-center justify-center"
                        >
                          <ArrowDownIcon className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {/* Date Tile */}
                      <DateTile iso={m.created_at} />

                      {/* Avatar */}
                      <div
                        className={`hidden sm:flex shrink-0 items-center justify-center rounded-xl border font-black ${tone}`}
                        style={{ width: 44, height: 44, fontSize: 14, letterSpacing: '-0.02em' }}
                      >
                        {initials(m.name)}
                      </div>

                      {/* Content */}
                      <button
                        onClick={() => setSelected(m)}
                        className="flex-1 text-left min-w-0"
                      >
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="text-base font-black text-gray-900 dark:text-white truncate">{m.name}</span>
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${meta.pillTone}`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} ${m.status === 'neu' ? 'animate-pulse' : ''}`} />
                            {meta.label}
                          </span>
                          {isOldest && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                              {days} Tage offen
                            </span>
                          )}
                          {isToday && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                              Heute eingegangen
                            </span>
                          )}
                          {dog && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-blue-500/30 bg-blue-500/15 text-blue-700 dark:text-blue-300">
                              <DogIcon className="w-3 h-3" />
                              Mit Hund
                            </span>
                          )}
                          {m.email_sent === 0 && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border border-orange-500/30 bg-orange-500/15 text-orange-300">
                              E-Mail-Fehler
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-slate-400 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                          <span className="inline-flex items-center gap-1.5">
                            <EnvelopeIcon className="w-3.5 h-3.5" />
                            {m.email}
                          </span>
                          {m.phone && (
                            <span className="inline-flex items-center gap-1.5">
                              <PhoneIcon className="w-3.5 h-3.5" />
                              {m.phone}
                            </span>
                          )}
                          {dog && (
                            <span className="inline-flex items-center gap-1.5">
                              <DogIcon className="w-3.5 h-3.5" />
                              {dog}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-700 dark:text-slate-300 mt-2 line-clamp-2">{m.message}</div>
                        <div className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-500">
                          Eingegangen: {formatDate(m.created_at)}
                          {m.status_changed_by && ` · Geändert von ${m.status_changed_by}`}
                        </div>
                      </button>

                      {/* Actions (desktop) */}
                      <div className="hidden md:flex flex-col gap-2 shrink-0">
                        <button
                          onClick={() => setSelected(m)}
                          className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-[11px] font-semibold transition inline-flex items-center justify-center gap-1.5"
                        >
                          <EyeIcon className="w-3.5 h-3.5" />
                          Details
                        </button>
                        <div className="flex gap-1.5">
                          <button
                            onClick={() => quickStatus(m.id, 'aktiv')}
                            className="w-9 h-9 rounded-xl bg-primary-500/10 border border-primary-500/30 text-primary-700 dark:text-primary-300 hover:bg-primary-500/20 transition flex items-center justify-center"
                            title="Annehmen"
                          >
                            <CheckCircleIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEmailTarget(m)}
                            className="w-9 h-9 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 transition flex items-center justify-center"
                            title="Kontaktieren"
                          >
                            <EnvelopeIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => quickStatus(m.id, 'abgelehnt')}
                            className="w-9 h-9 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 transition flex items-center justify-center"
                            title="Ablehnen"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(m.id)}
                            className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-gray-600 dark:text-slate-400 hover:bg-rose-500/10 hover:text-rose-300 hover:border-rose-500/30 transition flex items-center justify-center"
                            title="Löschen"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {/* Actions (mobile) */}
                      <div className="md:hidden flex flex-col gap-1.5 shrink-0">
                        <button
                          onClick={() => setSelected(m)}
                          className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 transition flex items-center justify-center"
                          title="Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(m.id)}
                          className="w-9 h-9 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 transition flex items-center justify-center"
                          title="Löschen"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-between text-xs text-gray-600 dark:text-slate-400">
              <span>
                Seite <span className="text-gray-900 dark:text-white font-bold">{page + 1}</span> von {totalPages} · {total}{' '}
                {total === 1 ? 'Eintrag' : 'Einträge'}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 inline-flex items-center gap-1 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="w-3.5 h-3.5" />
                  Zurück
                </button>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 inline-flex items-center gap-1 font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Weiter
                  <ChevronRightIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* STAT GRID — full status breakdown */}
        {visibleStats.length > 0 && (
          <section className="pb-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-primary-400/80 font-semibold mb-3">
              Verteilung nach Status
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {visibleStats.map(({ status, count }) => {
                const meta = STATUS_META[status];
                const active = statusFilter === status;
                return (
                  <button
                    key={status}
                    onClick={() => handleStatusFilter(active ? 'alle' : status)}
                    className={`text-left rounded-2xl p-3 border transition ${
                      active
                        ? 'bg-primary-500/15 border-primary-500/40'
                        : 'border-slate-700 hover:bg-white/[0.03]'
                    }`}
                    style={!active ? { background: CARD_BG } : undefined}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                      <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-semibold truncate">
                        {meta.label}
                      </span>
                    </div>
                    <div className={`text-2xl font-black tabular-nums ${active ? 'text-primary-300' : 'text-white'}`}>
                      {count}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Detail Modal */}
        {selected && (
          <DetailModal
            message={selected}
            onClose={() => setSelected(null)}
            onChange={(id) => refreshKeepingSelection(id)}
            onDelete={() => handleDelete(selected.id)}
            onContact={() => setEmailTarget(selected)}
          />
        )}

        {/* Email-Template Modal */}
        {emailTarget && (
          <EmailTemplateModal
            message={emailTarget}
            onClose={() => setEmailTarget(null)}
            onSent={() => {
              setEmailTarget(null);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Detail Modal ----------

interface DetailProps {
  message: WaitlistMessage;
  onClose: () => void;
  onChange: (id: number) => void;
  onDelete: () => void;
  onContact: () => void;
}

const DetailModal: React.FC<DetailProps> = ({ message, onClose, onChange, onDelete, onContact }) => {
  const [status, setStatus] = useState<Status>(message.status);
  const [notes, setNotes] = useState<string>(message.notes || '');
  const [contactedAt, setContactedAt] = useState<string>(
    message.contacted_at ? message.contacted_at.replace(' ', 'T').slice(0, 16) : ''
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setStatus(message.status);
    setNotes(message.notes || '');
    setContactedAt(message.contacted_at ? message.contacted_at.replace(' ', 'T').slice(0, 16) : '');
  }, [message]);

  const handleSave = async () => {
    setSaving(true);
    setErr(null);
    try {
      const payload: any = { status, notes };
      if (contactedAt) {
        payload.contactedAt = contactedAt.replace('T', ' ');
      } else {
        payload.contactedAt = null;
      }
      await apiClient.patch(`/api/waitlist/${message.id}`, payload);
      onChange(message.id);
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const dog = buildDogInfo(message);
  const days = daysSince(message.created_at);
  const tone = avatarTone(message.name);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-3xl flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 border-b border-slate-800">
          <div className="flex items-start gap-4 min-w-0">
            <div
              className={`shrink-0 flex items-center justify-center rounded-xl border font-black ${tone}`}
              style={{ width: 56, height: 56, fontSize: 18, letterSpacing: '-0.02em' }}
            >
              {initials(message.name)}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                Bewerber-Details
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white tracking-tight mt-1 truncate">{message.name}</h2>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${STATUS_META[message.status].pillTone}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${STATUS_META[message.status].dot}`} />
                  {STATUS_META[message.status].label} · {days} {days === 1 ? 'Tag' : 'Tage'} offen
                </span>
              </div>
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-1.5">
                Eingegangen am{' '}
                <span className="font-bold text-gray-900 dark:text-white">{formatDate(message.created_at)}</span>
                {message.ip_address && (
                  <>
                    {' '}· IP <span className="font-mono text-[11px]">{message.ip_address}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            aria-label="Schließen"
          >
            <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Stammdaten */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
              Stammdaten
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-900/40">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 inline-flex items-center gap-1">
                  <EnvelopeIcon className="w-3 h-3" />
                  E-Mail
                </div>
                <a
                  href={`mailto:${message.email}`}
                  className="block text-sm font-bold text-primary-700 dark:text-primary-300 hover:underline mt-0.5 truncate"
                >
                  {message.email}
                </a>
              </div>
              <div className="px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-900/40">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 inline-flex items-center gap-1">
                  <PhoneIcon className="w-3 h-3" />
                  Telefon
                </div>
                {message.phone ? (
                  <a
                    href={`tel:${message.phone}`}
                    className="block text-sm font-bold text-gray-900 dark:text-white hover:text-primary-300 mt-0.5 truncate"
                  >
                    {message.phone}
                  </a>
                ) : (
                  <div className="text-sm font-bold text-gray-500 dark:text-slate-500 mt-0.5">–</div>
                )}
              </div>
              <div className="px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-900/40">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 inline-flex items-center gap-1">
                  <DogIcon className="w-3 h-3" />
                  Hund
                </div>
                <div className="text-sm font-bold text-gray-900 dark:text-white mt-0.5 truncate">{dog || '–'}</div>
              </div>
              <div className="px-3 py-2.5 rounded-xl border border-slate-700 bg-slate-900/40">
                <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">IP-Adresse</div>
                <div className="text-sm font-mono text-gray-900 dark:text-white mt-0.5 truncate">{message.ip_address || '–'}</div>
              </div>
            </div>
          </div>

          {/* Nachricht */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
              Nachricht der Bewerberin
            </div>
            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-4 text-sm text-gray-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed">
              {message.message}
            </div>
          </div>

          {/* E-Mail-Status / unsubscribed */}
          {message.email_sent === 0 && message.email_error && (
            <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 p-3 text-sm text-orange-200">
              <div className="font-semibold mb-1">E-Mail-Versand fehlgeschlagen:</div>
              <div className="text-xs font-mono break-all text-orange-300/80">{message.email_error}</div>
            </div>
          )}
          {message.unsubscribed_at && (
            <div className="rounded-xl border border-slate-700 bg-slate-900/40 p-3 text-sm text-gray-700 dark:text-slate-300">
              Hat sich am {formatDate(message.unsubscribed_at)} selbst abgemeldet.
            </div>
          )}

          {/* Status setzen */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
              Status setzen
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_ORDER.map(s => {
                const meta = STATUS_META[s];
                const active = status === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5 transition ${
                      active
                        ? 'bg-primary-500 border-primary-500 text-slate-950'
                        : 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
            {message.status_changed_at && (
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-500">
                Zuletzt geändert {formatDate(message.status_changed_at)}
                {message.status_changed_by ? ` von ${message.status_changed_by}` : ''}
              </p>
            )}
          </div>

          {/* Kontaktiert am */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2">
              Kontaktiert am
            </label>
            <input
              type="datetime-local"
              value={contactedAt}
              onChange={(e) => setContactedAt(e.target.value)}
              className="w-full sm:w-72 px-3 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition"
              style={{ colorScheme: 'dark' }}
            />
          </div>

          {/* Notizen */}
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-2 inline-flex items-center gap-1">
              <PencilSquareIcon className="w-3 h-3" />
              Interne Notizen
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="z.B. Telefonat geführt, Termin vereinbart, Sonderwünsche …"
              className="w-full px-3 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-gray-900 dark:text-white placeholder:text-slate-500 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition resize-y"
            />
          </div>

          {err && (
            <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
              {err}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-between gap-3 bg-white dark:bg-[#1c2432]" >
          <div className="flex gap-2">
            <button
              onClick={onContact}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-500/10 border border-blue-500/30 text-blue-700 dark:text-blue-300 hover:bg-blue-500/20 text-xs font-semibold transition"
            >
              <EnvelopeIcon className="w-3.5 h-3.5" />
              Kontaktieren
            </button>
            <button
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20 text-xs font-semibold transition"
            >
              <TrashIcon className="w-3.5 h-3.5" />
              Löschen
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                  Speichern …
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-3.5 h-3.5" />
                  Speichern
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Email Template Modal ----------

interface EmailTemplateProps {
  message: WaitlistMessage;
  onClose: () => void;
  onSent: () => void;
}

const EmailTemplateModal: React.FC<EmailTemplateProps> = ({ message, onClose, onSent }) => {
  const [templateId, setTemplateId] = useState<string>(EMAIL_TEMPLATES[0].id);
  const [subject, setSubject] = useState<string>('');
  const [body, setBody] = useState<string>('');
  const [editing, setEditing] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const replacePlaceholders = useCallback((text: string): string => {
    const firstName = (message.name || '').trim().split(/\s+/)[0] || message.name || '';
    const dogStr = buildDogInfo(message);
    const hundZusatz = dogStr ? ` und ${message.dog_name || 'deinen Hund'}` : '';
    return text
      .replace(/\{\{name\}\}/g, firstName)
      .replace(/\{\{full_name\}\}/g, message.name || '')
      .replace(/\{\{hund\}\}/g, message.dog_name || '–')
      .replace(/\{\{rasse\}\}/g, message.dog_breed || '–')
      .replace(/\{\{hund_zusatz\}\}/g, hundZusatz)
      .replace(/\{\{email\}\}/g, message.email || '');
  }, [message]);

  // Apply selected template
  useEffect(() => {
    const tpl = EMAIL_TEMPLATES.find(t => t.id === templateId) || EMAIL_TEMPLATES[0];
    setSubject(replacePlaceholders(tpl.subject));
    setBody(replacePlaceholders(tpl.body));
    setEditing(false);
  }, [templateId, replacePlaceholders]);

  const handleSend = async () => {
    setSending(true);
    setErr(null);
    try {
      // Try sending email via potential endpoint; gracefully fallback to status-update
      try {
        await apiClient.post(`/api/waitlist/${message.id}/send-email`, {
          subject,
          body,
          template: templateId,
        });
      } catch (sendErr: any) {
        // Endpoint may not exist; we still flag as kontaktiert
        if (sendErr.response?.status !== 404) throw sendErr;
      }
      const now = new Date();
      const iso = now.toISOString().slice(0, 19).replace('T', ' ');
      await apiClient.patch(`/api/waitlist/${message.id}`, {
        status: 'kontaktiert',
        contactedAt: iso,
      });
      onSent();
    } catch (e: any) {
      setErr(e.response?.data?.message || 'Senden fehlgeschlagen');
    } finally {
      setSending(false);
    }
  };

  const tone = avatarTone(message.name);

  // Render preview body with highlighted placeholders (replaced values)
  const renderHighlightedPreview = (text: string): React.ReactNode => {
    // Re-derive replacements so we can wrap them visually
    const firstName = (message.name || '').trim().split(/\s+/)[0] || message.name || '';
    const replacements: Array<[string, string]> = [
      ['{{name}}', firstName],
      ['{{full_name}}', message.name || ''],
      ['{{hund}}', message.dog_name || '–'],
      ['{{rasse}}', message.dog_breed || '–'],
      ['{{email}}', message.email || ''],
    ];
    // Build a regex of non-empty replacement values, escape regex
    const escapes = replacements
      .map(([, v]) => v)
      .filter(v => v && v !== '–')
      .map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (escapes.length === 0) {
      return text.split('\n').map((line, i) => (
        <p key={i}>{line || ' '}</p>
      ));
    }
    const re = new RegExp(`(${escapes.join('|')})`, 'g');
    return text.split('\n').map((line, i) => {
      const parts = line.split(re);
      return (
        <p key={i}>
          {parts.length === 0 || (parts.length === 1 && parts[0] === '')
            ? ' '
            : parts.map((p, j) =>
                escapes.some(e => new RegExp(`^${e}$`).test(p)) ? (
                  <span
                    key={j}
                    className="bg-primary-500/15 text-primary-700 dark:text-primary-300 font-bold px-1 rounded"
                  >
                    {p}
                  </span>
                ) : (
                  <React.Fragment key={j}>{p}</React.Fragment>
                )
              )}
        </p>
      );
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(2,6,23,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="rounded-t-3xl sm:rounded-3xl w-full sm:max-w-4xl flex flex-col overflow-hidden bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700"
        style={{ boxShadow: '0 30px 60px -20px rgba(0,0,0,0.7)', maxHeight: 'calc(100vh - 32px)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-3 border-b border-slate-800">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className={`shrink-0 flex items-center justify-center rounded-xl border font-black ${tone}`}
              style={{ width: 44, height: 44, fontSize: 14, letterSpacing: '-0.02em' }}
            >
              {initials(message.name)}
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-primary-700 dark:text-primary-300 font-bold">
                E-Mail-Vorlage senden
              </div>
              <h4 className="text-xl font-black text-gray-900 dark:text-white tracking-tight truncate">An {message.name}</h4>
              <p className="text-[11px] text-gray-600 dark:text-slate-400 truncate">{message.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center transition shrink-0 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10"
            aria-label="Schließen"
          >
            <XMarkIcon className="w-4 h-4 text-gray-700 dark:text-slate-300" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 grid md:grid-cols-[1fr_1.6fr] gap-6">
          {/* Templates */}
          <div>
            <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-3">
              Vorlage auswählen
            </div>
            <div className="space-y-2">
              {EMAIL_TEMPLATES.map(tpl => {
                const active = templateId === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => setTemplateId(tpl.id)}
                    className={`w-full text-left px-4 py-3 rounded-2xl border transition ${
                      active
                        ? 'bg-primary-500/10 border-primary-500/40'
                        : 'bg-white/5 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className={`text-sm font-black ${active ? 'text-white' : 'text-white'}`}>{tpl.label}</div>
                      {active && <CheckCircleIcon className="w-4 h-4 text-primary-700 dark:text-primary-300" />}
                    </div>
                    <div className="text-[11px] text-gray-600 dark:text-slate-400">{tpl.desc}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-5 px-4 py-3 rounded-2xl border border-slate-700 bg-slate-900/40">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">
                Platzhalter ersetzen
              </div>
              <div className="mt-2 space-y-1.5 text-[11px] font-mono text-gray-700 dark:text-slate-300">
                <div>
                  <span className="text-primary-700 dark:text-primary-300">{'{{name}}'}</span> →{' '}
                  {(message.name || '').trim().split(/\s+/)[0] || '–'}
                </div>
                <div>
                  <span className="text-primary-700 dark:text-primary-300">{'{{hund}}'}</span> → {message.dog_name || '–'}
                </div>
                <div>
                  <span className="text-primary-700 dark:text-primary-300">{'{{rasse}}'}</span> → {message.dog_breed || '–'}
                </div>
                <div>
                  <span className="text-primary-700 dark:text-primary-300">{'{{email}}'}</span> → {message.email}
                </div>
              </div>
            </div>
          </div>

          {/* Vorschau */}
          <div className="min-w-0">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold">Vorschau</div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border border-emerald-500/30 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Live-Vorschau
              </span>
            </div>
            <div className="rounded-2xl border border-slate-700 bg-slate-900/40 overflow-hidden">
              {/* Mail header */}
              <div className="px-4 py-3 border-b border-slate-800 bg-slate-900/60 text-[11px] text-gray-600 dark:text-slate-400 space-y-1">
                <div>
                  <span className="text-gray-500 dark:text-slate-500">An:</span>{' '}
                  <span className="text-gray-900 dark:text-white font-bold">{message.email}</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-500">Von:</span>{' '}
                  <span className="text-gray-900 dark:text-white">vorstand@mein-hundesportverein.de</span>
                </div>
                <div>
                  <span className="text-gray-500 dark:text-slate-500">Betreff:</span>{' '}
                  {editing ? (
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      className="ml-1 px-2 py-1 rounded-lg bg-slate-900/60 border border-slate-700 text-gray-900 dark:text-white text-[11px] outline-none focus:border-primary-500 w-[80%]"
                    />
                  ) : (
                    <span className="text-gray-900 dark:text-white font-bold">{subject}</span>
                  )}
                </div>
              </div>
              <div className="p-5 text-sm text-gray-800 dark:text-slate-200 leading-relaxed space-y-3">
                {editing ? (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    rows={12}
                    className="w-full px-3 py-2.5 rounded-xl bg-slate-900/60 border border-slate-700 text-sm text-gray-900 dark:text-white outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/30 transition resize-y"
                  />
                ) : (
                  renderHighlightedPreview(body)
                )}
              </div>
            </div>

            {err && (
              <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
                {err}
              </div>
            )}

            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition"
              >
                Abbrechen
              </button>
              <button
                onClick={() => setEditing(e => !e)}
                className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-gray-800 dark:text-slate-200 hover:bg-white/10 text-xs font-semibold transition inline-flex items-center gap-1.5"
              >
                <PencilSquareIcon className="w-3.5 h-3.5" />
                {editing ? 'Vorschau' : 'Bearbeiten'}
              </button>
              <button
                onClick={handleSend}
                disabled={sending}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-400 text-slate-950 text-xs font-bold transition disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                    Senden …
                  </>
                ) : (
                  <>
                    <PaperAirplaneIcon className="w-3.5 h-3.5" />
                    Senden
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaitlistManagement;
